import * as React from "react";
import {format} from "date-fns";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import type {
  PostTrainingProps,
  trainingDataProps,
  trainingResonseProps,
  userTrainingCategoriesType,
  userTrainingDetailsProps,
} from "./trainingDialogStyle";
import {trainingDialogStyle} from "./trainingDialogStyle";
import {useAppSelector, store} from "../../../redux/store";
import {getRequest, setHeader, postRequest} from "repository/genericRepository";
import {FormButton} from "components/FormComponents/Button/FormButton";
import Container from "@mui/material/Container";
import {Loader} from "components/FormComponents/Loader/Loader";
import {
  USERTRAINING_ENDPOINT,
  TRAINING_CONTENT_URL,
} from "commonUtils/globalAPISettings";
import {
  handleAxiosErrors,
  initialMessage,
  isValidObject,
  loggedMessage,
  message,
} from "commonUtils/utils";
import Link from "@mui/material/Link";
import DialogTitleCommon, {DialogCloseCommon} from "../DialogTitleCommon";
import {
  Alerts,
  API_TIMEOUTS_MILLISECONDS,
  CPSMessages,
  PageNames,
} from "commonUtils/enumerations";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

interface Props {
  trainingopen: boolean;
  buttonCancel: string;
  buttonSubmit: string;
  handleCloseTraining: () => void;
  enableDisableCloseIcon: boolean | undefined;
}

const TrainingDialog: React.FC<Props> = ({
  trainingopen,
  buttonCancel,
  buttonSubmit,
  handleCloseTraining,
  enableDisableCloseIcon,
}) => {
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);
  const [trainingdata, setTrainingData] = React.useState<trainingDataProps[]>(
    [],
  );
  const [isAllChecked, setIsAllChecked] = React.useState<boolean>(false);
  const [isTrainingChecked, setIsTrainingChecked] =
    React.useState<boolean>(false);
  const [isAnyCheckboxChecked, setIsAnyCheckboxChecked] = React.useState<
    number[]
  >([]);
  const [loader, setLoader] = React.useState<boolean>(false);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const {userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const getUserTrainingRecord = React.useMemo(
    () => async () => {
      try {
        const authState = store.getState();
        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };
        setLoader(true);
        const responseData: trainingResonseProps = await getRequest({
          url:
            USERTRAINING_ENDPOINT +
            "?userId=" +
            authState.auth?.userDetails?.userId,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.USERTRAINING_ENDPOINT,
          params: {},
        });
        if (isValidObject(responseData)) {
          const object: trainingDataProps[] = [];
          let index: number = -1;
          responseData.userTrainingCategories.forEach(
            (x: userTrainingCategoriesType) => {
              if (!x.isSubCategory) {
                index += 1;
                const y: trainingDataProps = {
                  CompletedDate: null,
                  isTraining: false,
                  id: x.id,
                  subCategory: [],
                  isSubCategory: x.isSubCategory,
                  displayText: x.displayText,
                  isChecked: false,
                };
                object.push(y);
              } else {
                if (
                  object[index].subCategory &&
                  object[index].subCategory.length >= 0
                ) {
                  object[index].subCategory.push(x.displayText);
                } else {
                  object[index].subCategory = [x.displayText];
                }
              }
            },
          );

          const propertyValues = Object.values(
            responseData.userTrainingDetails,
          );

          object.filter(
            (
              x: {
                CompletedDate: string | null;
                isTraining: boolean;
                isChecked: boolean;
              },
              i: number,
            ) => {
              x.CompletedDate = propertyValues[i];
              x.isTraining = propertyValues[i + (index + 1)];
              x.isChecked = x.isTraining;
              return x;
            },
          );
          setIsAllChecked(object.every(item => item.isTraining));
          setIsTrainingChecked(
            isTrainingCompleted(responseData.userTrainingDetails),
          );
          setTrainingData(object);
        }
        setLoader(false);
      } catch (error: any) {
        loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
        handleAxiosErrors(error, setShowAlert, setServerValidation);
        setLoader(false);
      }
    },
    [trainingdata],
  );

  React.useEffect(() => {
    try {
      if (trainingopen) {
        getUserTrainingRecord();
      }
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
      setLoader(false);
    }
  }, [trainingopen]);

  const isTrainingCompleted = (trainingStatus: userTrainingDetailsProps) => {
    if (
      trainingStatus?.isTrainingCPSOverviewCompleted ??
      trainingStatus?.isTrainingMarketingMedicalCompleted ??
      trainingStatus?.isTrainingDiscoveryMedicalCompleted ??
      trainingStatus?.isTrainingApproversCompleted
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleCheckBoxChange = (id: number) => {
    const newData = trainingdata.map(item => {
      if (item?.id === id) {
        const findId = isAnyCheckboxChecked.findIndex(x => x === id);
        if (findId === -1) {
          isAnyCheckboxChecked.push(id);
          setIsAnyCheckboxChecked(isAnyCheckboxChecked);
        } else {
          isAnyCheckboxChecked.splice(findId, 1);
          setIsAnyCheckboxChecked(isAnyCheckboxChecked);
        }
        return {...item, isTraining: !item?.isTraining};
      }
      return item;
    });
    setTrainingData(newData);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      if (isAnyCheckboxChecked.length > 0) {
        const checkedDetails = {
          userId: userDetails?.userId,
          profileId: userDetails?.profileId,
          isTrainingCPSOverviewCompleted: trainingdata[0]?.isTraining,
          isTrainingMarketingMedicalCompleted: trainingdata[1]?.isTraining,
          isTrainingDiscoveryMedicalCompleted: trainingdata[2]?.isTraining,
          isTrainingApproversCompleted: trainingdata[3]?.isTraining,
        };
        postUserTraining(checkedDetails);
      } else {
        setShowAlert(true);
        setServerValidation({
          message: CPSMessages.PLEASE_SELECT_ONE_CHECKBOX,
          messageCode: 0,
          isSuccessMessage: false,
          isValidationMessage: false,
          isErrorMessage: true,
        });
      }
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
      setLoader(false);
    }
  };

  const postUserTraining = async (details: PostTrainingProps) => {
    try {
      const authState = store.getState();
      const {
        userId,
        profileId,
        isTrainingCPSOverviewCompleted,
        isTrainingMarketingMedicalCompleted,
        isTrainingDiscoveryMedicalCompleted,
        isTrainingApproversCompleted,
      } = details;
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const responseData = await postRequest({
        url: USERTRAINING_ENDPOINT,
        headers: setHeader(APIHeaders),
        params: {
          userId,
          profileId,
          isTrainingCPSOverviewCompleted,
          isTrainingMarketingMedicalCompleted,
          isTrainingDiscoveryMedicalCompleted,
          isTrainingApproversCompleted,
        },
      });
      handleCloseTraining();
      if (isValidObject(responseData)) {
        if (responseData?.messageCode !== 0) {
          setShowAlert(true);
          setServerValidation({
            message: CPSMessages.SOMETHING_WENT_WRONG,
            messageCode: 0,
            isSuccessMessage: false,
            isValidationMessage: false,
            isErrorMessage: true,
          });
        } else {
          setShowAlert(false);
          setServerValidation(initialMessage);
        }
      }
    } catch (error: any) {
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);

      handleAxiosErrors(error, setShowAlert, setServerValidation);
      setLoader(false);
    }
  };

  return (
    <>
      <Loader open={loader} />
      {!loader && (
        <Dialog
          maxWidth="xl"
          PaperProps={{
            sx: {
              minHeight: "47.4rem",
            },
          }}
          open={trainingopen}>
          <DialogTitleCommon title="Training" />
          {(!enableDisableCloseIcon || isTrainingChecked) && (
            <DialogCloseCommon onClick={handleCloseTraining} />
          )}
          <DialogContent sx={trainingDialogStyle.dialogContent}>
            <Container component="main" className="training-dialog-container">
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid
                  container
                  spacing={1}
                  sx={trainingDialogStyle.gridContainer}>
                  {showAlert && serverValidation?.message && (
                    <Grid item xs={12} sx={trainingDialogStyle.gridItem}>
                      <FormValidationAlert
                        closeIcon={true}
                        showAlertBox={showAlert}
                        setShowAlertBox={setShowAlert}
                        severity={
                          (serverValidation?.isSuccessMessage ?? false)
                            ? Alerts.SUCCESS
                            : (serverValidation?.isValidationMessage ?? false)
                              ? Alerts.INFO
                              : Alerts.ERROR
                        }
                        message={serverValidation?.message}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={trainingDialogStyle.trainingMuiBox}>
                      <Typography sx={trainingDialogStyle.headerTextStyle}>
                        Please take the appropriate training(s) based on your
                        role and the tasks you need to perform/complete in CPS.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={trainingDialogStyle.trainingMuiBox}>
                      <Typography sx={trainingDialogStyle.headerTextStyle}>
                        <ul>
                          <li>
                            General CPS use: CPS Overview Training required
                          </li>
                          <li>
                            Adding (Registering) Activities: CPS Overview
                            Training and review of applicable sections in CPS
                            User Guide required (User Guide located on the
                            Resources tab)
                          </li>
                          <li>Approver: Approver Training required</li>
                        </ul>
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={trainingDialogStyle.trainingMuiBox}>
                      <Typography sx={trainingDialogStyle.linkclick}>
                        Click
                        <Link
                          sx={trainingDialogStyle.linkclickhere}
                          target="_blank"
                          href={`${TRAINING_CONTENT_URL}`}>
                          HERE
                        </Link>
                        to start the CPS Overview and/or Approver Training.
                        Return to this page to acknowledge completion.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={trainingDialogStyle.trainingMuiBox}>
                      <Typography sx={trainingDialogStyle.headerTextStyle}>
                        For users also adding (registering) activities, review
                        the CPS User Guide on the Resources tab. Acknowledge
                        completion on this page to update your CPS access for
                        activity registration.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={trainingDialogStyle.trainingMuiBox}>
                      <Typography sx={trainingDialogStyle.headerTextStyle}>
                        Training is a one-time requirement for Lilly users and
                        annual for suppliers.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer>
                      <Table
                        size="small"
                        sx={trainingDialogStyle.tableBorder}
                        aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={trainingDialogStyle.tableCellOne}
                              align="left">
                              <Typography
                                sx={trainingDialogStyle.TableCellStyle}>
                                Training Tasks
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={trainingDialogStyle.tableCellTwo}
                              align="center">
                              <Typography
                                sx={trainingDialogStyle.TableCellStyle}>
                                Completed Training Modules
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={trainingDialogStyle.tableCellThree}
                              align="center">
                              <Typography
                                sx={trainingDialogStyle.TableCellStyle}>
                                Last completed
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {trainingdata.map(row => (
                            <TableRow key={row.id}>
                              <TableCell
                                align="left"
                                sx={trainingDialogStyle.tableCellBorder}>
                                <Typography
                                  sx={trainingDialogStyle.TableRowParent}>
                                  {row?.displayText}
                                </Typography>
                                {row?.subCategory.map(
                                  (r: string, i: number) => (
                                    <Typography
                                      key={i}
                                      sx={trainingDialogStyle.TableRowChild}>
                                      {r}
                                    </Typography>
                                  ),
                                )}
                              </TableCell>
                              <TableCell
                                sx={trainingDialogStyle.tableCellBorder}
                                align="center">
                                <Checkbox
                                  sx={trainingDialogStyle.trainingCheckBox}
                                  disabled={row.isChecked}
                                  onChange={() => {
                                    handleCheckBoxChange(row.id);
                                  }}
                                  checked={row.isTraining}
                                />
                              </TableCell>
                              <TableCell
                                sx={trainingDialogStyle.tableCellBorder}
                                align="center">
                                <Typography
                                  sx={trainingDialogStyle.dateTraining}>
                                  {row.CompletedDate
                                    ? format(
                                        new Date(row.CompletedDate),
                                        "MM/dd/yyyy",
                                      )
                                    : ""}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={trainingDialogStyle.trainingButton}>
                      {!enableDisableCloseIcon || isTrainingChecked ? (
                        <FormButton
                          size="small"
                          type="submit"
                          variant="contained"
                          disabled={false}
                          onClick={handleCloseTraining}
                          outlet={buttonCancel}
                        />
                      ) : (
                        <FormButton
                          size="small"
                          type="submit"
                          variant="contained"
                          disabled={true}
                          onClick={handleCloseTraining}
                          outlet={buttonCancel}
                        />
                      )}

                      <FormButton
                        size="small"
                        type="submit"
                        variant="contained"
                        disabled={isAllChecked}
                        outlet={buttonSubmit}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default TrainingDialog;
